import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  Code as CodeIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getRecipe,
  updateRecipe,
  deleteRecipe,
  flagRecipe,
  unflagRecipe,
  removeRecipeIngredient,
  updateRecipeIngredient,
  addRecipeIngredient,
  searchIngredients,
  deleteRecipeStep,
  addRecipeStep,
  updateRecipeStep,
  getActiveChefs,
  addRecipeTag,
  removeRecipeTag,
  getDietaryTags,
  regenerateRecipeImage,
  Recipe,
  RecipeStep,
  Chef,
  Ingredient,
} from '../adminApi';
import { generateRecipeHtml } from './recipeHtmlExport';

const RecipeEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [prepTime, setPrepTime] = useState<number | ''>('');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePhotographer, setImagePhotographer] = useState('');
  const [imagePhotographerUrl, setImagePhotographerUrl] = useState('');
  const [chefId, setChefId] = useState<number | ''>('');
  const [activeChefs, setActiveChefs] = useState<Chef[]>([]);

  // Step editing state
  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  const [editStepData, setEditStepData] = useState<{
    step_number: number;
    header: string;
    description: string;
  }>({ step_number: 1, header: '', description: '' });

  // Add step form state
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStepData, setNewStepData] = useState<{
    header: string;
    description: string;
  }>({ header: '', description: '' });

  // Ingredient editing state — inline quantity/unit edit on an existing row
  const [editingIngredientId, setEditingIngredientId] = useState<number | null>(null);
  const [editIngredientData, setEditIngredientData] = useState<{
    quantity: string;
    unit: string;
  }>({ quantity: '', unit: '' });

  // Add-ingredient form state — Autocomplete-driven ingredient lookup
  const [showAddIngredient, setShowAddIngredient] = useState(false);
  const [newIngredient, setNewIngredient] = useState<Ingredient | null>(null);
  const [newIngredientQuantity, setNewIngredientQuantity] = useState('');
  const [newIngredientUnit, setNewIngredientUnit] = useState('');
  const [ingredientOptions, setIngredientOptions] = useState<Ingredient[]>([]);
  const [ingredientSearchInput, setIngredientSearchInput] = useState('');
  const [searchingIngredients, setSearchingIngredients] = useState(false);

  // Tag state
  const [allTags, setAllTags] = useState<{ tag_id: number; tag_name: string; type: string }[]>([]);

  // Image regeneration state
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await getRecipe(parseInt(id));
        setRecipe(data);
        setName(data.name || '');
        setDescription(data.description || '');
        setInstructions(data.instructions || '');
        setPrepTime(data.prep_time || '');
        setImageUrl(data.image_url || '');
        setImagePhotographer(data.image_photographer || '');
        setImagePhotographerUrl(data.image_photographer_url || '');
        setChefId((data as any).chef_id || '');
      } catch (err: any) {
        setError(err.message || 'Failed to load recipe');
      } finally {
        setLoading(false);
      }
    };

    const fetchChefs = async () => {
      try {
        const chefs = await getActiveChefs();
        setActiveChefs(chefs);
      } catch {
        // Non-critical — dropdown just won't have options
      }
    };

    const fetchTags = async () => {
      try {
        const tags = await getDietaryTags();
        setAllTags(tags);
      } catch {
        // Non-critical
      }
    };

    fetchRecipe();
    fetchChefs();
    fetchTags();
  }, [id]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await updateRecipe(parseInt(id), {
        name,
        description,
        instructions,
        prep_time: prepTime || undefined,
        image_url: imageUrl || null,
        image_photographer: imagePhotographer || null,
        image_photographer_url: imagePhotographerUrl || null,
        chef_id: chefId || null,
      } as any);
      setSuccess('Recipe saved successfully');
      // Refresh recipe data
      const data = await getRecipe(parseInt(id));
      setRecipe(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  const handleFlag = async () => {
    if (!recipe) return;
    try {
      if (recipe.flagged_for_review) {
        await unflagRecipe(recipe.recipe_id);
      } else {
        const reason = prompt('Enter reason for flagging:');
        if (!reason) return;
        await flagRecipe(recipe.recipe_id, reason);
      }
      // Refresh
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!recipe) return;
    if (!window.confirm(`Are you sure you want to delete "${recipe.name}"?`)) return;

    try {
      await deleteRecipe(recipe.recipe_id);
      navigate('/admin/recipes');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveIngredient = async (riId: number) => {
    if (!recipe) return;
    if (!window.confirm('Remove this ingredient from the recipe?')) return;

    try {
      await removeRecipeIngredient(recipe.recipe_id, riId);
      // Refresh
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditIngredient = (ing: { recipe_ingredient_id: number; quantity: number; unit: string }) => {
    setEditingIngredientId(ing.recipe_ingredient_id);
    setEditIngredientData({
      quantity: String(ing.quantity ?? ''),
      unit: ing.unit ?? '',
    });
  };

  const handleSaveIngredient = async () => {
    if (!recipe || editingIngredientId === null) return;
    const qtyNum = parseFloat(editIngredientData.quantity);
    if (Number.isNaN(qtyNum)) {
      setError('Quantity must be a number.');
      return;
    }
    try {
      await updateRecipeIngredient(recipe.recipe_id, editingIngredientId, {
        quantity: qtyNum,
        unit: editIngredientData.unit,
      });
      setEditingIngredientId(null);
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('Ingredient updated');
    } catch (err: any) {
      setError(err.message || 'Failed to update ingredient');
    }
  };

  const handleCancelEditIngredient = () => {
    setEditingIngredientId(null);
  };

  // Debounced-ish ingredient search — fires on each input change. The /admin/
  // ingredients endpoint is paginated; we cap at 20 results which is plenty
  // for typeahead.
  useEffect(() => {
    if (!showAddIngredient) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearchingIngredients(true);
      try {
        const res = await searchIngredients({
          query: ingredientSearchInput || undefined,
          page: 1,
          limit: 20,
        });
        if (!cancelled) setIngredientOptions(res.ingredients || []);
      } catch {
        if (!cancelled) setIngredientOptions([]);
      } finally {
        if (!cancelled) setSearchingIngredients(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ingredientSearchInput, showAddIngredient]);

  const handleAddIngredient = async () => {
    if (!recipe || !newIngredient) return;
    const qtyNum = parseFloat(newIngredientQuantity);
    if (Number.isNaN(qtyNum)) {
      setError('Quantity must be a number.');
      return;
    }
    try {
      await addRecipeIngredient(recipe.recipe_id, {
        ingredient_id: newIngredient.ingredient_id,
        quantity: qtyNum,
        unit: newIngredientUnit,
      });
      setShowAddIngredient(false);
      setNewIngredient(null);
      setNewIngredientQuantity('');
      setNewIngredientUnit('');
      setIngredientSearchInput('');
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('Ingredient added');
    } catch (err: any) {
      setError(err.message || 'Failed to add ingredient');
    }
  };

  const handleDeleteStep = async (stepId: number) => {
    if (!recipe) return;
    if (!window.confirm('Delete this step?')) return;

    try {
      await deleteRecipeStep(recipe.recipe_id, stepId);
      // Refresh
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditStep = (step: RecipeStep) => {
    setEditingStepId(step.step_id);
    setEditStepData({
      step_number: step.step_number,
      header: step.header || '',
      description: step.description,
    });
  };

  const handleSaveStep = async () => {
    if (!recipe || !editingStepId) return;
    try {
      await updateRecipeStep(recipe.recipe_id, editingStepId, {
        step_number: editStepData.step_number,
        header: editStepData.header || null,
        description: editStepData.description,
      });
      setEditingStepId(null);
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('Step updated');
    } catch (err: any) {
      setError(err.message || 'Failed to update step');
    }
  };

  const handleCancelEdit = () => {
    setEditingStepId(null);
  };

  const handleAddStep = async () => {
    if (!recipe || !newStepData.description.trim()) return;
    const nextStepNumber = (recipe.steps?.length || 0) + 1;
    try {
      await addRecipeStep(recipe.recipe_id, {
        step_number: nextStepNumber,
        description: newStepData.description,
        header: newStepData.header || undefined,
      });
      setShowAddStep(false);
      setNewStepData({ header: '', description: '' });
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('Step added');
    } catch (err: any) {
      setError(err.message || 'Failed to add step');
    }
  };

  const handleAddTag = async (tagName: string) => {
    if (!recipe || !tagName.trim()) return;
    try {
      await addRecipeTag(recipe.recipe_id, tagName);
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('Tag added');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add tag');
    }
  };

  const handleRemoveTag = async (recipeTagId: number) => {
    if (!recipe) return;
    try {
      await removeRecipeTag(recipe.recipe_id, recipeTagId);
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('Tag removed');
    } catch (err: any) {
      setError(err.message || 'Failed to remove tag');
    }
  };

  const handleExportHtml = async () => {
    if (!recipe) return;

    try {
      // Use current form values for export (may differ from saved recipe)
      const recipeForExport = {
        ...recipe,
        name,
        description,
        instructions,
        prep_time: prepTime || recipe.prep_time,
        image_url: imageUrl || null,
        image_photographer: imagePhotographer || null,
        image_photographer_url: imagePhotographerUrl || null,
      };
      const html = generateRecipeHtml(recipeForExport);
      await navigator.clipboard.writeText(html);
      setSuccess('HTML copied to clipboard!');
    } catch (err: any) {
      setError('Failed to copy to clipboard: ' + err.message);
    }
  };

  const handleToggleImageLock = async () => {
    if (!recipe) return;
    try {
      const newLocked = !recipe.image_locked;
      await updateRecipe(recipe.recipe_id, { image_locked: newLocked } as any);
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess(newLocked ? 'Image locked — DALL-E batch will skip this recipe' : 'Image unlocked');
    } catch (err: any) {
      setError(err.message || 'Failed to toggle image lock');
    }
  };

  const handleRegenerateImage = async () => {
    if (!recipe) return;
    if (!window.confirm('Generate a new DALL-E image for this recipe? This will replace the current image.')) return;

    setRegenerating(true);
    setError(null);
    try {
      const { image_url } = await regenerateRecipeImage(recipe.recipe_id);
      setImageUrl(image_url);
      setImagePhotographer('');
      setImagePhotographerUrl('');
      const data = await getRecipe(recipe.recipe_id);
      setRecipe(data);
      setSuccess('New image generated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate image');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!recipe) {
    return <Alert severity="error">Recipe not found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/admin/recipes')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" flexGrow={1}>
          Edit Recipe #{recipe.recipe_id}
          {(recipe as any).is_modular && (
            <Chip label="Modular" color="secondary" size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
          )}
        </Typography>
        <Button
          variant="outlined"
          color="info"
          startIcon={<CodeIcon />}
          onClick={handleExportHtml}
        >
          Export HTML
        </Button>
        <Button
          variant="outlined"
          color={recipe.flagged_for_review ? 'warning' : 'inherit'}
          startIcon={<FlagIcon />}
          onClick={handleFlag}
        >
          {recipe.flagged_for_review ? 'Unflag' : 'Flag'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          Delete
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {recipe.flagged_for_review && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This recipe is flagged: {recipe.flag_reason}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Recipe Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Prep Time (minutes)"
                  type="number"
                  value={prepTime}
                  onChange={(e) => setPrepTime(e.target.value ? parseInt(e.target.value) : '')}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Cook Time (minutes)"
                  type="number"
                  value={recipe.cook_time ?? '—'}
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Total Time (minutes)"
                  type="number"
                  value={recipe.total_time ?? '—'}
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Photographer Name"
                  value={imagePhotographer}
                  onChange={(e) => setImagePhotographer(e.target.value)}
                  fullWidth
                  placeholder="e.g., John Smith"
                  helperText="For Pexels attribution"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Photographer URL"
                  value={imagePhotographerUrl}
                  onChange={(e) => setImagePhotographerUrl(e.target.value)}
                  fullWidth
                  placeholder="e.g., https://www.pexels.com/@johnsmith"
                  helperText="Link to photographer's Pexels profile"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Chef Attribution</InputLabel>
                  <Select
                    value={chefId}
                    label="Chef Attribution"
                    onChange={(e) => setChefId(e.target.value as number | '')}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {activeChefs.map((c) => (
                      <MenuItem key={c.chef_id} value={c.chef_id}>
                        {c.name} ({c.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Instructions (legacy field)"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  helperText="This is the legacy instructions field. Prefer using steps below."
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Steps */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Steps ({recipe.steps?.length || 0})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowAddStep(true)}
                disabled={showAddStep}
              >
                Add Step
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Ensure steps only reference ingredients listed in this recipe.
            </Typography>
            {recipe.steps && recipe.steps.length > 0 ? (
              <Stack spacing={2}>
                {recipe.steps.map((step) => (
                  <Card key={step.step_id} variant="outlined">
                    <CardContent>
                      {editingStepId === step.step_id ? (
                        // Edit mode
                        <Box>
                          <Box display="flex" gap={2} mb={2}>
                            <TextField
                              label="Step #"
                              type="number"
                              value={editStepData.step_number}
                              onChange={(e) =>
                                setEditStepData({
                                  ...editStepData,
                                  step_number: parseInt(e.target.value) || 1,
                                })
                              }
                              size="small"
                              sx={{ width: 80 }}
                            />
                            <TextField
                              label="Header (optional)"
                              value={editStepData.header}
                              onChange={(e) =>
                                setEditStepData({ ...editStepData, header: e.target.value })
                              }
                              size="small"
                              sx={{ flexGrow: 1 }}
                              placeholder="e.g., Prep, Cooking"
                            />
                          </Box>
                          <TextField
                            label="Description"
                            value={editStepData.description}
                            onChange={(e) =>
                              setEditStepData({ ...editStepData, description: e.target.value })
                            }
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<CheckIcon />}
                              onClick={handleSaveStep}
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CloseIcon />}
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        // View mode
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flexGrow={1}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Step {step.step_number}
                              {step.header && ` - ${step.header}`}
                            </Typography>
                            <Typography variant="body2">{step.description}</Typography>
                          </Box>
                          <Box display="flex" gap={0.5}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditStep(step)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteStep(step.step_id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No steps defined
              </Typography>
            )}

            {/* Add Step Form */}
            {showAddStep && (
              <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Add New Step
                  </Typography>
                  <TextField
                    label="Header (optional)"
                    value={newStepData.header}
                    onChange={(e) => setNewStepData({ ...newStepData, header: e.target.value })}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder="e.g., Prep, Cooking"
                  />
                  <TextField
                    label="Description"
                    value={newStepData.description}
                    onChange={(e) =>
                      setNewStepData({ ...newStepData, description: e.target.value })
                    }
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    sx={{ mb: 2 }}
                    required
                  />
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddStep}
                      disabled={!newStepData.description.trim()}
                    >
                      Add Step
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setShowAddStep(false);
                        setNewStepData({ header: '', description: '' });
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Image Preview */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                Image Preview
              </Typography>
              {recipe.image_locked && (
                <Chip label="Locked" size="small" color="warning" icon={<LockIcon />} />
              )}
            </Box>
            {imageUrl && (
              <Box
                component="img"
                src={imageUrl}
                alt={name}
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  maxHeight: 200,
                  objectFit: 'cover',
                  mb: 1,
                }}
                onError={(e: any) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            {!imageUrl && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No image
              </Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color={recipe.image_locked ? 'warning' : 'inherit'}
                startIcon={recipe.image_locked ? <LockIcon /> : <LockOpenIcon />}
                onClick={handleToggleImageLock}
              >
                {recipe.image_locked ? 'Unlock' : 'Lock'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={regenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRegenerateImage}
                disabled={regenerating}
              >
                {regenerating ? 'Generating...' : 'Regenerate'}
              </Button>
            </Stack>
          </Paper>

          {/* Usage Stats */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Usage Stats
            </Typography>
            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Used in meals:
                </Typography>
                <Typography variant="body2">{recipe.usage?.meal_count || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  In meal plans:
                </Typography>
                <Typography variant="body2">{recipe.usage?.plan_count || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Total usage count:
                </Typography>
                <Typography variant="body2">{recipe.usage_count || 0}</Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Tags */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Dietary Tags
            </Typography>
            {recipe.tags && recipe.tags.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                {recipe.tags.map((tag) => (
                  <Chip
                    key={tag.recipe_tag_id}
                    label={tag.tag_name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => handleRemoveTag(tag.recipe_tag_id)}
                  />
                ))}
              </Stack>
            )}
            <Autocomplete
              freeSolo
              options={allTags
                .map((t) => t.tag_name)
                .filter((name) => !recipe.tags?.some((rt) => rt.tag_name === name))
              }
              renderInput={(params) => (
                <TextField {...params} label="Add tag" size="small" placeholder="Type or select a tag" />
              )}
              onChange={(_e, value) => {
                if (value) handleAddTag(value);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter') {
                  const input = (e.target as HTMLInputElement).value;
                  if (input && !allTags.some((t) => t.tag_name === input)) {
                    handleAddTag(input);
                  }
                }
              }}
              value={null}
              blurOnSelect
              clearOnBlur
              size="small"
            />
          </Paper>

          {/* Nutrition */}
          {recipe.nutrition_info && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Nutrition (per serving)
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(recipe.nutrition_info.per_serving || recipe.nutrition_info).filter(([, value]) => typeof value !== 'object').map(([key, value]) => (
                  <Box key={key} display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}:
                    </Typography>
                    <Typography variant="body2">{String(value)}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Ingredients (full width) */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Ingredients ({recipe.ingredients?.length || 0})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowAddIngredient(true)}
                disabled={showAddIngredient}
              >
                Add Ingredient
              </Button>
            </Box>
            {recipe.ingredients && recipe.ingredients.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ingredient</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipe.ingredients.map((ing) => {
                    const isEditing = editingIngredientId === ing.recipe_ingredient_id;
                    return (
                      <TableRow key={ing.recipe_ingredient_id}>
                        <TableCell>{ing.name}</TableCell>
                        <TableCell>
                          <Chip label={ing.category || 'Other'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={editIngredientData.quantity}
                              onChange={(e) =>
                                setEditIngredientData((d) => ({ ...d, quantity: e.target.value }))
                              }
                              sx={{ width: 80 }}
                              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                            />
                          ) : (
                            ing.quantity
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              value={editIngredientData.unit}
                              onChange={(e) =>
                                setEditIngredientData((d) => ({ ...d, unit: e.target.value }))
                              }
                              sx={{ width: 100 }}
                              placeholder="cup, tbsp, oz..."
                            />
                          ) : (
                            ing.unit
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <>
                              <IconButton size="small" color="primary" onClick={handleSaveIngredient}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={handleCancelEditIngredient}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditIngredient(ing)}
                                disabled={editingIngredientId !== null}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveIngredient(ing.recipe_ingredient_id)}
                                disabled={editingIngredientId !== null}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No ingredients defined (this is a stub recipe)
              </Typography>
            )}

            {showAddIngredient && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Ingredient
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Autocomplete
                    sx={{ minWidth: 280 }}
                    size="small"
                    options={ingredientOptions}
                    value={newIngredient}
                    onChange={(_, v) => setNewIngredient(v)}
                    inputValue={ingredientSearchInput}
                    onInputChange={(_, v) => setIngredientSearchInput(v)}
                    getOptionLabel={(opt) => opt.name}
                    isOptionEqualToValue={(a, b) => a.ingredient_id === b.ingredient_id}
                    loading={searchingIngredients}
                    renderInput={(params) => (
                      <TextField {...params} label="Search ingredient" placeholder="peanut butter..." />
                    )}
                  />
                  <TextField
                    size="small"
                    label="Quantity"
                    value={newIngredientQuantity}
                    onChange={(e) => setNewIngredientQuantity(e.target.value)}
                    sx={{ width: 100 }}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  />
                  <TextField
                    size="small"
                    label="Unit"
                    value={newIngredientUnit}
                    onChange={(e) => setNewIngredientUnit(e.target.value)}
                    placeholder="cup, tbsp, oz..."
                    sx={{ width: 140 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CheckIcon />}
                    onClick={handleAddIngredient}
                    disabled={!newIngredient || !newIngredientQuantity}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setShowAddIngredient(false);
                      setNewIngredient(null);
                      setNewIngredientQuantity('');
                      setNewIngredientUnit('');
                      setIngredientSearchInput('');
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecipeEditor;
